import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Map, Calculator, ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { PROJECTS } from '../../constants';
import { Project } from '../../types';
import SectionHeading from '../UI/SectionHeading';
import ProjectCircuitBoard from '../UI/ProjectCircuitBoard';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const IconMap: Record<string, React.ReactNode> = {
  BarChart3: <BarChart3 size={36} strokeWidth={1.25} />,
  Map: <Map size={36} strokeWidth={1.25} />,
  Calculator: <Calculator size={36} strokeWidth={1.25} />,
};

// One accent across all projects — the work varies, the voice doesn't
const THEME = {
  text: 'text-accent',
  dot: 'bg-accent',
  wash: 'bg-accent/[0.04]',
  iconHover: 'group-hover:text-accent',
  border: 'border-accent/20',
};

const BAR_HEIGHTS = [34, 58, 42, 76, 50, 88, 64, 96, 70, 46, 82, 38];

const MAP_HUB = { x: 42, y: 30 };
const MAP_NODES = [
  { x: 12, y: 14 },
  { x: 20, y: 42 },
  { x: 34, y: 10 },
  { x: 62, y: 16 },
  { x: 78, y: 40 },
  { x: 88, y: 14 },
  { x: 58, y: 46 },
];

const CALC_KEYS = ['7', '8', '9', '÷', '4', '5', '6', '×', '1', '2', '3', '='];
const CALC_SEQUENCE = ['4', '8', '2', '÷', '5', '='];
const CALC_AMOUNT = 482500;

const BANNER_CHIPS: Record<string, string> = {
  'cb-rejection': 'Rejections ↓ · Daily refresh',
  'branch-locator': 'Branch network · Live view',
  'gold-loan-calculator': 'Instant eligibility · 0 errors',
};

const Banner: React.FC<{ id: string }> = ({ id }) => {
  if (id === 'cb-rejection') {
    return (
      <div className='absolute inset-x-0 top-0 bottom-0 flex items-end gap-1.5 sm:gap-2 px-8 pt-16'>
        {BAR_HEIGHTS.map((h, i) => (
          <div
            key={i}
            className={`bar flex-1 rounded-t-md will-change-transform ${
              i % 4 === 1 ? 'bg-accent/40' : 'bg-ink/[0.07]'
            } ${i % 4 === 2 ? 'bar-live' : ''} group-hover:bg-accent/60 transition-colors duration-700`}
            style={{ height: `${h}%`, transitionDelay: `${i * 25}ms` }}
          />
        ))}
      </div>
    );
  }

  if (id === 'branch-locator') {
    return (
      <div className='absolute inset-0'>
        <div
          className='absolute inset-0 opacity-[0.55]'
          style={{
            backgroundImage:
              'linear-gradient(rgb(var(--ink) / 0.07) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--ink) / 0.07) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />
        <svg
          className='absolute inset-0 w-full h-full'
          viewBox='0 0 100 56'
          preserveAspectRatio='xMidYMid slice'
          aria-hidden='true'
        >
          {MAP_NODES.map((n, i) => (
            <line
              key={`l${i}`}
              className='map-link'
              x1={MAP_HUB.x}
              y1={MAP_HUB.y}
              x2={n.x}
              y2={n.y}
              stroke='#9a948b'
              strokeOpacity='0.45'
              strokeWidth='1.25'
              strokeLinecap='round'
              pathLength={1}
              style={{ vectorEffect: 'non-scaling-stroke' }}
            />
          ))}
          {MAP_NODES.map((n, i) => (
            <circle
              key={`r${i}`}
              className='map-ring'
              cx={n.x}
              cy={n.y}
              r={1.7}
              fill='none'
              stroke='#9a948b'
              strokeWidth='0.6'
              opacity='0'
            />
          ))}
          {MAP_NODES.map((n, i) => (
            <circle key={`n${i}`} className='map-node' cx={n.x} cy={n.y} r={1.7} fill='#9a948b' />
          ))}
          <circle
            className='map-hub-ring'
            cx={MAP_HUB.x}
            cy={MAP_HUB.y}
            r={2.6}
            fill='none'
            stroke='#ff2d00'
            strokeWidth='0.7'
            opacity='0'
          />
          <circle className='map-hub' cx={MAP_HUB.x} cy={MAP_HUB.y} r={2.6} fill='#ff2d00' />
        </svg>
      </div>
    );
  }

  return (
    <div className='absolute inset-0 flex items-center justify-center'>
      <div className='absolute w-80 h-80 rounded-full bg-accent/[0.07] blur-3xl' />
      <div className='calc-device relative w-[82%] max-w-[380px] rounded-2xl border border-ink/10 bg-paper/85 backdrop-blur-sm shadow-[0_18px_40px_-18px_rgba(0,0,0,0.25)] p-4 sm:p-5'>
        <div className='label-mono !text-[10px] text-gray2 flex items-center justify-between mb-3'>
          <span>Gold Loan</span>
          <span className='text-accent'>22K · Live rate</span>
        </div>
        <div className='calc-display rounded-lg border border-accent/30 bg-accent/[0.05] h-11 sm:h-12 flex items-center justify-end px-4 font-mono text-base sm:text-lg text-accent'>
          ₹&nbsp;
          <span className='calc-amount'>{CALC_AMOUNT.toLocaleString('en-IN')}</span>
        </div>
        <div className='grid grid-cols-4 gap-2 mt-3'>
          {CALC_KEYS.map((k, i) => (
            <div
              key={i}
              className={`calc-key aspect-[5/4] rounded-lg border flex items-center justify-center font-mono text-sm will-change-transform ${
                k === '=' ? 'border-accent/40 bg-accent/10 text-accent' : 'border-ink/10 text-ink/35'
              }`}
            >
              {k}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ProjectsProps {
  onProjectSelect: (project: Project) => void;
  isDetailed?: boolean;
}

const ProjectCard: React.FC<{
  project: Project;
  index: number;
  onSelect: () => void;
}> = ({ project, index, onSelect }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const theme = THEME;

  useGSAP(
    () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const card = cardRef.current;
      if (!card) return;
      const q = gsap.utils.selector(card);

      const art = q('.banner-art')[0];
      if (art) {
        gsap.fromTo(
          art,
          { yPercent: -6 },
          {
            yPercent: 6,
            ease: 'none',
            scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: 0.4 },
          }
        );
      }

      const enter = { trigger: card, start: 'top 80%', once: true } as const;

      if (project.id === 'cb-rejection') {
        gsap.fromTo(
          q('.bar'),
          { scaleY: 0 },
          {
            scaleY: 1,
            transformOrigin: '50% 100%',
            duration: 1,
            ease: 'power3.out',
            stagger: 0.045,
            scrollTrigger: enter,
          }
        );
        gsap.to(q('.bar-live'), {
          scaleY: () => gsap.utils.random(0.55, 1.12),
          transformOrigin: '50% 100%',
          duration: 1.5,
          repeat: -1,
          yoyo: true,
          repeatRefresh: true,
          ease: 'sine.inOut',
          stagger: 0.4,
          delay: 1.6,
        });
      }

      if (project.id === 'branch-locator') {
        gsap.fromTo(
          q('.map-link'),
          { strokeDasharray: 1, strokeDashoffset: 1 },
          {
            strokeDashoffset: 0,
            duration: 1.1,
            ease: 'power2.out',
            stagger: 0.12,
            scrollTrigger: enter,
          }
        );
        gsap.fromTo(
          q('.map-node'),
          { attr: { r: 0 } },
          {
            attr: { r: 1.7 },
            duration: 0.6,
            ease: 'back.out(2.5)',
            stagger: 0.18,
            delay: 0.2,
            scrollTrigger: enter,
          }
        );
        gsap.fromTo(
          q('.map-ring'),
          { attr: { r: 1.7 }, opacity: 0.6 },
          {
            attr: { r: 6 },
            opacity: 0,
            duration: 2,
            repeat: -1,
            ease: 'power1.out',
            stagger: 0.4,
            delay: 1.2,
            scrollTrigger: enter,
          }
        );
        gsap.fromTo(
          q('.map-hub-ring'),
          { attr: { r: 2.6 }, opacity: 0.7 },
          {
            attr: { r: 9 },
            opacity: 0,
            duration: 2.2,
            repeat: -1,
            ease: 'power1.out',
            delay: 0.8,
            scrollTrigger: enter,
          }
        );
      }

      if (project.id === 'gold-loan-calculator') {
        const keys = q('.calc-key');
        gsap.fromTo(
          q('.calc-device'),
          { autoAlpha: 0, y: 26, scale: 0.94 },
          { autoAlpha: 1, y: 0, scale: 1, duration: 0.8, ease: 'power3.out', scrollTrigger: enter }
        );
        gsap.to(q('.calc-device'), {
          yPercent: -2.5,
          duration: 2.6,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: 1,
        });
        gsap.fromTo(
          keys,
          { autoAlpha: 0, scale: 0.8 },
          {
            autoAlpha: 1,
            scale: 1,
            duration: 0.5,
            ease: 'back.out(1.8)',
            stagger: { each: 0.04, from: 'random' },
            delay: 0.2,
            scrollTrigger: enter,
          }
        );

        const amountEl = q('.calc-amount')[0];
        if (amountEl) {
          const state = { v: 0 };
          amountEl.textContent = '0';
          gsap.to(state, {
            v: CALC_AMOUNT,
            duration: 1.8,
            ease: 'power3.out',
            delay: 0.4,
            scrollTrigger: enter,
            onUpdate: () => {
              amountEl.textContent = Math.round(state.v).toLocaleString('en-IN');
            },
          });
        }

        const pressTl = gsap.timeline({
          repeat: -1,
          repeatDelay: 2.4,
          delay: 1.8,
          scrollTrigger: enter,
        });
        CALC_SEQUENCE.forEach(ch => {
          const el = keys[CALC_KEYS.indexOf(ch)];
          if (!el) return;
          pressTl
            .to(
              el,
              { scale: 0.88, duration: 0.11, yoyo: true, repeat: 1, ease: 'power2.inOut' },
              '+=0.22'
            )
            .to(
              el,
              { backgroundColor: 'rgba(255,45,0,0.12)', duration: 0.11, yoyo: true, repeat: 1 },
              '<'
            );
        });
      }

      if (window.matchMedia('(pointer: fine)').matches) {
        const banner = q('.banner-tilt')[0];
        if (banner) {
          gsap.set(banner, { transformPerspective: 900 });
          const rx = gsap.quickTo(banner, 'rotationX', { duration: 0.6, ease: 'power3' });
          const ry = gsap.quickTo(banner, 'rotationY', { duration: 0.6, ease: 'power3' });
          const move = (e: MouseEvent) => {
            const r = banner.getBoundingClientRect();
            rx(-((e.clientY - r.top) / r.height - 0.5) * 5);
            ry(((e.clientX - r.left) / r.width - 0.5) * 7);
          };
          const leave = () => {
            rx(0);
            ry(0);
          };
          banner.addEventListener('mousemove', move);
          banner.addEventListener('mouseleave', leave);
          return () => {
            banner.removeEventListener('mousemove', move);
            banner.removeEventListener('mouseleave', leave);
          };
        }
      }
    },
    { scope: cardRef }
  );

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay: (index % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onClick={onSelect}
      data-cursor='pointer'
      className='group cursor-pointer'
    >
      {/* Banner */}
      <div className='banner-tilt relative rounded-2xl sm:rounded-3xl bg-surface overflow-hidden aspect-[16/9] sm:aspect-[2/1] will-change-transform'>
        <div
          className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ${theme.wash}`}
        />
        <div className='banner-art absolute inset-0 will-change-transform'>
          <Banner id={project.id} />
        </div>

        <div
          className={`absolute left-5 top-5 sm:left-7 sm:top-7 text-ink/50 ${theme.iconHover} transition-colors duration-500`}
        >
          {IconMap[project.icon]}
        </div>

        <div className='absolute left-5 bottom-5 sm:left-7 sm:bottom-7 label-mono !text-[10px] inline-flex items-center gap-2 bg-paper/90 backdrop-blur-sm border border-line rounded-full px-3 py-1.5 text-gray1'>
          <span className={`relative flex h-1.5 w-1.5`}>
            <span
              className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${theme.dot}`}
            />
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${theme.dot}`} />
          </span>
          {BANNER_CHIPS[project.id]}
        </div>

        <div className='absolute right-5 top-5 sm:right-7 sm:top-7 w-11 h-11 rounded-full bg-paper border border-line flex items-center justify-center text-ink opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500'>
          <ArrowUpRight
            size={18}
            className='group-hover:rotate-45 transition-transform duration-500'
          />
        </div>
      </div>

      {/* Caption */}
      <div className='flex items-start justify-between gap-4 pt-5 pb-5 border-b border-line'>
        <div className='min-w-0'>
          <h3 className='font-medium tracking-tight text-lg sm:text-xl text-ink flex items-baseline gap-3 flex-wrap'>
            {project.title}
            <span className='label-mono text-gray2 hidden sm:inline'>— {project.subtitle}</span>
          </h3>
          <p className='mt-2 text-sm text-gray1 leading-relaxed max-w-xl'>{project.description}</p>
          <div className='mt-4 flex flex-wrap items-center gap-2'>
            {project.tags.map(tag => (
              <span
                key={tag}
                className='label-mono !text-[10px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-line text-gray1 group-hover:border-ink/30 transition-colors duration-300'
              >
                <span className={`h-1 w-1 rounded-full ${theme.dot}`} />
                {tag}
              </span>
            ))}
          </div>
          <div className='mt-4 flex flex-wrap gap-x-6 gap-y-2'>
            {project.stats.slice(0, 3).map(stat => (
              <div key={stat.label} className='flex items-baseline gap-1.5'>
                <span className={`font-medium tracking-tight ${theme.text}`}>{stat.value}</span>
                <span className='text-xs text-gray2'>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
        <span className={`label-mono shrink-0 mt-1 ${theme.text}`}>
          [ {String(index + 1).padStart(2, '0')} ]
        </span>
      </div>
    </motion.div>
  );
};

// ── Detailed / full-page view ──────────────────────────────────────────────
const ProjectDetailItem: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
  const theme = THEME;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Header rule */}
      <div className='flex items-center gap-5 mb-6'>
        <span className={`label-mono font-medium text-sm shrink-0 ${theme.text}`}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <div className='flex-1 h-px bg-line' />
      </div>

      <div className='mb-1'>
        <h3 className='font-medium tracking-tight text-2xl sm:text-3xl text-ink'>
          {project.title}
        </h3>
        <p className='label-mono text-gray2 mt-1'>{project.subtitle}</p>
      </div>

      {/* Body: circuit board visual + details */}
      <div className='mt-8 grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-5 sm:gap-8 lg:gap-12'>
        {/* Circuit board banner */}
        <div className='rounded-2xl bg-surface overflow-hidden aspect-video relative'>
          <ProjectCircuitBoard id={project.id} />
          {/* Status chip */}
          <div className='absolute left-4 bottom-4 sm:left-5 sm:bottom-5 label-mono !text-[10px] inline-flex items-center gap-2 bg-paper/90 backdrop-blur-sm border border-line rounded-full px-3 py-1.5 text-gray1'>
            <span className='relative flex h-1.5 w-1.5'>
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${theme.dot}`} />
              <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${theme.dot}`} />
            </span>
            {BANNER_CHIPS[project.id]}
          </div>
          {/* Architecture label */}
          <div className='absolute right-4 top-4 sm:right-5 sm:top-5 label-mono !text-[9px] text-gray2 bg-paper/80 backdrop-blur-sm border border-line rounded-full px-2.5 py-1'>
            System Architecture
          </div>
        </div>

        {/* Details panel */}
        <div className='flex flex-col gap-5'>
          <p className='text-base text-gray1 leading-relaxed'>{project.description}</p>

          {project.details?.features && project.details.features.length > 0 && (
            <ul className='space-y-2.5 pt-1'>
              {project.details.features.map((feat, i) => (
                <li key={i} className='flex items-start gap-3 text-sm text-gray1 leading-relaxed'>
                  <span className={`mt-[7px] h-1.5 w-1.5 rounded-full shrink-0 ${theme.dot}`} />
                  {feat}
                </li>
              ))}
            </ul>
          )}

          <div className='flex flex-wrap gap-2 pt-1'>
            {project.tags.map(tag => (
              <span
                key={tag}
                className='label-mono !text-[10px] px-2.5 py-1 rounded-full border border-line text-gray1'
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className='mt-auto pt-6 border-t border-line grid grid-cols-3 gap-2 sm:gap-4'>
            {project.stats.map(stat => (
              <div key={stat.label}>
                <div className={`font-medium tracking-tight text-xl sm:text-2xl ${theme.text}`}>
                  {stat.value}
                </div>
                <div className='text-xs text-gray2 mt-0.5 leading-snug'>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Challenge */}
      {project.details?.challenges && (
        <div className={`mt-8 p-5 sm:p-6 rounded-2xl border ${theme.border} bg-surface`}>
          <p className={`label-mono mb-2 ${theme.text}`}>Challenge</p>
          <p className='text-sm text-gray1 leading-relaxed'>{project.details.challenges}</p>
        </div>
      )}
    </motion.div>
  );
};

const Projects: React.FC<ProjectsProps> = ({ onProjectSelect, isDetailed = false }) => {
  if (isDetailed) {
    return (
      <div className='space-y-20'>
        {PROJECTS.map((project, index) => (
          <ProjectDetailItem key={project.id} project={project} index={index} />
        ))}
      </div>
    );
  }

  return (
    <section id='projects' className='scroll-mt-20 max-w-7xl mx-auto px-5 sm:px-6'>
      <SectionHeading
        index='04'
        kicker='Selected Work'
        title='Built inside the bank, used every day'
        description='Three internal tools shipped with HTML, CSS and JavaScript into real banking workflows, together cutting manual reporting effort by ~30% and giving branches real-time visibility. Click any card for the full case study.'
      />

      <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-12'>
        {PROJECTS.map((project, index) => (
          <div key={project.id}>
            <ProjectCard
              project={project}
              index={index}
              onSelect={() => onProjectSelect(project)}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default Projects;
